import { convertBinaryToYesNo, formatDate, getCurrentlyText, getCountryName, splitStringToArray } from "../../../utils/utils";
import { ConfirmationData, createFieldsGroups, Question } from "./confirmationData";
export const NO_DATA = "--"
interface MultiLanguageText {
    es: string;
    en: string;
    pt: string;
}
interface OptionValue {
    en: string;
    es: string;
    pt: string;
}

interface Option {
    key: string;
    value: OptionValue;
}
interface Field {
    question_id: string;
    order: number;
    title: MultiLanguageText;
    value: string;
    value_default: string;
    options: Option[];
    ui: string;
    hidden: boolean;
}

export interface Group {
    group_id: string;
    order: number;
    title: MultiLanguageText;
    fields: Field[];
    hidden: boolean;
}

interface Section {
    section_id: string;
    order: number;
    title: MultiLanguageText;
    groups: Group[];
    hidden: boolean;
}

export interface PageJson {
    page_id: string;
    status: string;
    sections: Section[];
}

export interface ApplicationSummary {
    order: number;
    description: string;
    mark: string;
    template_page_id: string;
    json: PageJson;
    id: number;
    quote_form_id: number;
}

// Constantes para tipos de UI
const UI_TYPES = {
    MULTIPLE_CHOICE: "multiplechoice",
    SIMPLE_CHOICE: "simplechoice",
    BUTTON_GROUP: "buttonGroup",
    DROPDOWN: "dropdown",
    DATEPICKER: "datepicker",
    DATEINPUTPICKER: "dateinputpicker",
    INPUT_TEXT: "inputText",
    INPUT_TEXT_AREA: "inputTextArea",
    AMOUNT_CONVERTER: "amountConverter",
    TEL: "tel",
    RADIO: "radio",
    WEIGHT_UNIT: "weightUnitInput",
    HEIGHT_UNIT: "heightUnitInput",
    DEEP_UNIT: "deepUnitInput",
    INPUT_AMOUNT: "inputAmount",
    FILE_SEARCH: "fileSearch",
    SWITCH: "switch",
    PERCENTAGE: "percentage",
    LABEL: "label",
    ADD: "add",
    CPF: "cpf",
    COUNTRY: "country",
    CEP: "cep"
} as const;

// Procesa campos con opciones
function processOptionField(field: Field, group: Group, isHidden: boolean, questionValueMap: Map<string, Question>, language: 'en' | 'es' | 'pt' = 'es'): void {
    const fieldValue = getFieldOptionValueByLanguage(field, field.value, language);
    questionValueMap.set(field.question_id, { value: fieldValue, hidden: isHidden, label: field.title[language] });
    questionValueMap.set(group.group_id + "|" + field.question_id, { value: fieldValue, hidden: isHidden, label: field.title[language] });
}

// Procesa campos de selección múltiple
function processMultipleChoice(field: Field, isHidden: boolean, questionValueMap: Map<string, Question>, language: 'en' | 'es' | 'pt' = 'es'): void {
    const choosenOptions = splitStringToArray(field.value);
    const optionValues = choosenOptions
        .map(element => getFieldOptionValueByLanguage(field, element, language))
        .filter(value => value !== "");
    questionValueMap.set(field.question_id, { value: optionValues.join(", "), hidden: isHidden, label: field.title[language] });

    // Procesar todas las opciones disponibles
    field.options.forEach(opt => {
        const isChosen = choosenOptions.includes(opt.key);
        questionValueMap.set(field.question_id + "-opt" + opt.key, {
            value: isChosen ? "SI" : "NO",
            hidden: isHidden,
            label: opt.value[language]
        });
    });
}

// Procesa campos de medidas
function processMeasurementField(field: Field, group: Group, isHidden: boolean, questionValueMap: Map<string, Question>, language: 'en' | 'es' | 'pt' = 'es'): void {


    questionValueMap.set(group.group_id + "|" + field.question_id, { value: field.value.split(',')[0], hidden: isHidden, label: field.title[language] });

    const result = processMeasurementValue(field.value)
    result.forEach((value, key) => {
        const measurementValue = value + " " + key;
        questionValueMap.set(group.group_id + "|" + field.question_id + "-" + key, { value: key !== "ft" ? measurementValue : value, hidden: isHidden, label: field.title[language] });
        questionValueMap.set(field.question_id + "-" + key, { value: key !== "ft" ? measurementValue : value, hidden: isHidden, label: field.title[language] });
    });
}

// Procesa un campo individual y actualiza el mapa de valores
function processField(field: Field, group: Group, isHidden: boolean, questionValueMap: Map<string, Question>, language: 'en' | 'es' | 'pt' = 'es', currency: string = 'USD'): void {
    console.log("processField language", language);
    if (field.ui !== "fileSearch") {
        field.value = field.value === "" && field.value_default !== "" ? field.value_default : field.value;
    }

    if (field.options && field.options.length > 0 && field.ui !== UI_TYPES.INPUT_TEXT) {
        if (field.ui === UI_TYPES.LABEL) {
            // Ignorar campos de tipo LABEL
            // return;
        }
        questionValueMap.set(group.group_id + "|" + field.question_id + "-raw", { value: field.value, hidden: false });

        switch (field.ui) {
            case UI_TYPES.MULTIPLE_CHOICE:
                processMultipleChoice(field, isHidden, questionValueMap, language);
                break;
            case UI_TYPES.SIMPLE_CHOICE:
            case UI_TYPES.BUTTON_GROUP:
            case UI_TYPES.DROPDOWN:
                processOptionField(field, group, isHidden, questionValueMap, language);
                const choosenOptions = splitStringToArray(field.value);

                // Procesar todas las opciones disponibles
                field.options.forEach(opt => {
                    const isChosen = choosenOptions.includes(opt.key);
                    questionValueMap.set(field.question_id + "-opt" + opt.key, {
                        value: isChosen ? "SI" : "NO",
                        hidden: isHidden,
                        label: opt.value[language]
                    });
                });
                break;
            case UI_TYPES.SWITCH:
                const radioValue = convertBinaryToYesNo(field.value, language);
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: radioValue, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: radioValue, hidden: isHidden, label: field.title[language] });
                break;
        }
    } else {
        switch (field.ui) {
            case UI_TYPES.DATEPICKER:
            case UI_TYPES.DATEINPUTPICKER:
                const dateValue = formatDate(field.value, "pt", true);
                const dv = dateValue === "" ? NO_DATA : dateValue;
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: dv, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: dv, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.INPUT_TEXT:
            case UI_TYPES.INPUT_TEXT_AREA:
            case UI_TYPES.FILE_SEARCH:
            case UI_TYPES.CEP:
                const value = field.value === "" ? NO_DATA : field.value;
                questionValueMap.set(group.group_id + "|" + field.question_id, { value, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.COUNTRY:
                const countryName = field.value === "" ? NO_DATA : getCountryName(field.value);
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: countryName, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: countryName, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.AMOUNT_CONVERTER:
                processAmountConverterField(field, group, isHidden, questionValueMap, language, currency);
                break;
            case UI_TYPES.PERCENTAGE:
                const percentageValue = field.value === "" ? NO_DATA : field.value + "%";
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: percentageValue, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: percentageValue, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.CPF:
                const cpfValue = field.value === "" ? NO_DATA : formatCPF(field.value);
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: cpfValue, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: cpfValue, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.TEL:
                const formattedPhone = formatPhoneNumber(field.value);
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: formattedPhone, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: formattedPhone, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.RADIO:
            case UI_TYPES.SWITCH:
                const radioValue = convertBinaryToYesNo(field.value, language);
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: radioValue, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: radioValue, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.WEIGHT_UNIT:
            case UI_TYPES.HEIGHT_UNIT:
            case UI_TYPES.DEEP_UNIT:
                processMeasurementField(field, group, isHidden, questionValueMap, language);
                break;
            case UI_TYPES.INPUT_AMOUNT:
                const amountValue = `${currency}${field.value}`;
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: amountValue, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: amountValue, hidden: isHidden, label: field.title[language] });
                break;
            case UI_TYPES.LABEL:
                questionValueMap.set(group.group_id + "|" + field.question_id, { value: field.value, hidden: isHidden, label: field.title[language] });
                questionValueMap.set(field.question_id, { value: field.value, hidden: isHidden, label: field.title[language] });
                break;
        }
    }

}

export function createQuestionValueMap(pageJson: PageJson, language: 'en' | 'es' | 'pt' = 'es', currency: string = 'USD'): Map<string, Question> {
    const questionValueMap = new Map<string, Question>();

    pageJson.sections?.forEach(section => {
        // Ocultar solo el título si contiene 'Proporciona más información' en español
        const shouldHideSectionTitle = section.title?.es?.includes('Proporciona más información');

        questionValueMap.set(section.section_id, { value: '', hidden: section.hidden || shouldHideSectionTitle, label: section.title[language] });
        section.groups?.forEach(group => {
            questionValueMap.set(group.group_id, { value: '', hidden: section.hidden || group.hidden, label: group.title[language] });
            group.fields?.forEach(field => {
                const isHidden = section.hidden || group.hidden || field.hidden;
                processField(field, group, isHidden, questionValueMap, language, currency);
            });
        });
    });
    return questionValueMap;
}

export function mapToTupleArray(map: Map<string, Question>): Array<[string, Question]> {
    return Array.from(map.entries()).map(([key, value]) => [key, value]);
}


export function getFieldOptionValueByLanguage(field: Field, value: string, language: 'en' | 'es' | 'pt'): string {
    const option = field.options.find(opt => opt.key === value);
    return option?.value[language] || "";
}

function formatCPF(cpf: string): string {
    if (!cpf) return "";

    // Remove any existing spaces, dots, or hyphens
    const cleaned = cpf.replace(/[^\d]/g, "");

    // Check if we have exactly 11 digits for a valid CPF
    if (cleaned.length !== 11) return cpf; // Return original if not 11 digits

    // Format as XXX.XXX.XXX-XX
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
}

function formatPhoneNumber(phone: string): string {
    if (!phone) return "";

    const cleaned = phone.replace(/[^\d+]/g, "");

    if (cleaned.startsWith("+")) {
        const countryCode = cleaned.slice(0, 3);
        const rest = cleaned.slice(3);

        if (countryCode === '+55') {
            // A partir de 7 dígitos totales después de +55, aparece el guion
            if (rest.length >= 7) {
                const areaCode = rest.slice(0, 2);
                const localNumber = rest.slice(2);

                if (localNumber.length === 9) {
                    // Para números locales de 9 dígitos: AA NNNNN-NNNN
                    const firstPart = localNumber.slice(0, 5);
                    const secondPart = localNumber.slice(5, 9);
                    return `${countryCode} ${areaCode} ${firstPart}-${secondPart}`;
                } else if (localNumber.length >= 5) {
                    // Para números locales de 5-8 dígitos: AA NNNN-XXXX
                    const firstPart = localNumber.slice(0, 4);
                    const secondPart = localNumber.slice(4);
                    return `${countryCode} ${areaCode} ${firstPart}-${secondPart}`;
                }
            }
            // Si no cumple las condiciones para guion, formato genérico para +55
            return `${countryCode} ${rest.slice(0, 2)} ${rest.slice(2)}`;
        }

        // Formato internacional para otros códigos de país
        return `${countryCode} ${rest.slice(0, 2)} ${rest.slice(2)}`;
    }

    // Si no tiene código de país, formato genérico
    return cleaned.length >= 2 ? `${cleaned.slice(0, 2)} ${cleaned.slice(2)}` : cleaned;
}

export function processMeasurementValue(input: string): Map<string, string> {

    if (!input) {
        return new Map();
    }

    const [measurement, value] = input.split(',');
    if (!measurement || !value) {
        return new Map();
    }
    const result = new Map<string, string>();
    const numericMatch = measurement.match(/[\d.]+/);
    const unitMatch = measurement.match(/[a-zA-Z]+/);

    if (!numericMatch || !unitMatch) {
        return new Map();
    }

    const numericValue = parseFloat(numericMatch[0]);
    const unit = unitMatch[0].toLowerCase();

    if (unit === 'kg' || unit === 'lb') {
        if (unit === 'kg') {
            result.set('kg', numericValue.toString());
            result.set('lb', (numericValue * 2.20462).toFixed(2));
        } else {
            result.set('lb', numericValue.toString());
            result.set('kg', (numericValue / 2.20462).toFixed(2));
        }
    } else if (unit === 'm' || unit === 'ft') {
        if (unit === 'm') {
            result.set('m', numericValue.toString());
            const totalFeet = numericValue * 3.28084;
            const feet = Math.floor(totalFeet);
            const inches = Math.round((totalFeet - feet) * 12);
            result.set('ft', `${feet} pies ${inches} pulgadas`);
        } else {
            const feet = Math.floor(numericValue);
            const inches = Math.round((numericValue - feet) * 12);
            result.set('ft', `${feet} pies ${inches} pulgadas`);
            result.set('m', (numericValue / 3.28084).toFixed(2));
        }
    }
    return result;
}

// Nueva función para procesar valores de AMOUNT_CONVERTER
function processAmountConverterField(field: Field, group: Group, isHidden: boolean, questionValueMap: Map<string, Question>, language: 'en' | 'es' | 'pt' = 'es', currency: string = 'USD'): void {
    if (!field.value || field.value === "") {
        // Si no hay valor, agregar entradas con NO_DATA
        const annualLabel = language === 'es' ? 'Ingreso anual' : language === 'en' ? 'Annual Income' : 'Renda anual';
        const monthlyLabel = language === 'es' ? 'Ingreso mensual' : language === 'en' ? 'Monthly Income' : 'Renda mensal';

        questionValueMap.set(`${group.group_id}|${field.question_id}-annual`, { value: NO_DATA, hidden: isHidden, label: annualLabel });
        questionValueMap.set(`${field.question_id}-annual`, { value: NO_DATA, hidden: isHidden, label: annualLabel });
        questionValueMap.set(`${group.group_id}|${field.question_id}-monthly`, { value: NO_DATA, hidden: isHidden, label: monthlyLabel });
        questionValueMap.set(`${field.question_id}-monthly`, { value: NO_DATA, hidden: isHidden, label: monthlyLabel });
        return;
    }

    const [frequency, amount] = field.value.split(',');
    if (!frequency || !amount) {
        // Si el formato no es válido, usar el valor original
        const value = field.value === "" ? NO_DATA : field.value;
        questionValueMap.set(group.group_id + "|" + field.question_id, { value, hidden: isHidden, label: field.title[language] });
        questionValueMap.set(field.question_id, { value, hidden: isHidden, label: field.title[language] });
        return;
    }

    const numericAmount = parseFloat(amount);
    const isAnnual = frequency.toUpperCase() === 'A';

    // Calcular ambos valores
    const annualAmount = isAnnual ? numericAmount : numericAmount * 12;
    const monthlyAmount = isAnnual ? numericAmount / 12 : numericAmount;

    // Formatear con moneda
    const formattedAnnual = `${currency}${annualAmount.toLocaleString()}`;
    const formattedMonthly = `${currency}${monthlyAmount.toLocaleString()}`;

    // Etiquetas según idioma
    const annualLabel = language === 'es' ? 'Ingreso anual' : language === 'en' ? 'Annual Income' : 'Renda anual';
    const monthlyLabel = language === 'es' ? 'Ingreso mensual' : language === 'en' ? 'Monthly Income' : 'Renda mensal';

    // Agregar ambas entradas al map
    questionValueMap.set(`${group.group_id}|${field.question_id}-annual`, { value: formattedAnnual, hidden: isHidden, label: annualLabel });
    questionValueMap.set(`${field.question_id}-annual`, { value: formattedAnnual, hidden: isHidden, label: annualLabel });
    questionValueMap.set(`${group.group_id}|${field.question_id}-monthly`, { value: formattedMonthly, hidden: isHidden, label: monthlyLabel });
    questionValueMap.set(`${field.question_id}-monthly`, { value: formattedMonthly, hidden: isHidden, label: monthlyLabel });
}

export const getUpdatedItems = (page: PageJson, sectionId: string, groupId: string, defaultItems: Map<string, ConfirmationData>, isEqual: boolean = true): Map<string, ConfirmationData> => {
    const section = page.sections.find(section => section.section_id === sectionId);
    if (!section) {
        return new Map([]);
    }
    const groups = section.groups.filter(group => isEqual ? group.group_id === groupId : group.group_id !== groupId).sort((a, b) => a.order - b.order);

    const result = createFieldsGroups(groups, defaultItems);
    return result;
};

export const findValue = (page: PageJson | undefined, sectionId: string, groupId: string, questionId: string): string => {
    if (!page) {
        return "";
    }
    const section = page.sections.find(section => section.section_id === sectionId);
    if (!section) {
        return "";
    }
    const groups = section.groups.find(group => group.group_id === groupId)
    if (!groups) {
        return "";
    }

    const field = groups.fields.find(field => field.question_id === questionId);
    return field?.value || "";
};

export function getPageIdByTemplateId(applicationSummary: ApplicationSummary[], templatePageId: string): number | undefined {
    const page = applicationSummary.find(page => page.template_page_id === templatePageId);
    return page?.id;
}

export const applyOverwrites = (cd: Map<string, ConfirmationData>, overWrite: Map<string, ConfirmationData>, page?: PageJson): Map<string, ConfirmationData> => {
    const result = new Map(cd);

    // Primero procesamos los hiddenAll
    overWrite.forEach((overwriteData, key) => {
        if (!overwriteData || !key) return; // Validación inicial

        if (overwriteData.hiddenAll) {
            // Verificar si la key corresponde a una sección
            if (page && page.sections) {
                const section = page.sections.find(section => section?.section_id === key);
                if (section) {
                    // Ocultar la sección
                    if (result.has(key)) {
                        const originalData = result.get(key)!;
                        result.set(key, {
                            ...originalData,
                            hidden: true,
                            color: overwriteData.color,
                            label: overwriteData.label,
                            type: overwriteData.type || originalData.type
                        });
                    }

                    // Ocultar todos los grupos de la sección
                    section.groups?.forEach(group => {
                        if (!group || !group.group_id) return; // Validación del grupo

                        if (result.has(group.group_id)) {
                            const originalData = result.get(group.group_id)!;
                            result.set(group.group_id, {
                                ...originalData,
                                hidden: true,
                                color: overwriteData.color,
                                label: overwriteData.label,
                                type: overwriteData.type || originalData.type
                            });
                        }

                        // Ocultar todos los campos de cada grupo
                        group.fields?.forEach(field => {
                            if (!field || !field.question_id) return; // Validación del campo

                            const fieldKey = `${group.group_id}|${field.question_id}`;
                            if (result.has(fieldKey)) {
                                const originalData = result.get(fieldKey)!;
                                result.set(fieldKey, {
                                    ...originalData,
                                    hidden: true,
                                    color: overwriteData.color,
                                    label: overwriteData.label,
                                    type: overwriteData.type || originalData.type
                                });
                            }
                        });
                    });
                } else {
                    // Si no es una sección, verificar si es un grupo
                    const group = page.sections
                        .filter(section => section && section.groups) // Validación de secciones
                        .flatMap(section => section.groups)
                        .find(group => group && group.group_id === key);

                    if (group) {
                        // Ocultar el grupo
                        if (result.has(key)) {
                            const originalData = result.get(key)!;
                            result.set(key, {
                                ...originalData,
                                hidden: true,
                                color: overwriteData.color,
                                label: overwriteData.label,
                                type: overwriteData.type || originalData.type
                            });
                        }

                        // Ocultar todos los campos del grupo
                        group.fields?.forEach(field => {
                            if (!field || !field.question_id) return; // Validación del campo

                            const fieldKey = `${group.group_id}|${field.question_id}`;
                            if (result.has(fieldKey)) {
                                const originalData = result.get(fieldKey)!;
                                result.set(fieldKey, {
                                    ...originalData,
                                    hidden: true,
                                    color: overwriteData.color,
                                    label: overwriteData.label,
                                    type: overwriteData.type || originalData.type
                                });
                            }
                        });
                    } else {
                        // Si no es ni sección ni grupo, ocultar solo el elemento específico
                        if (result.has(key)) {
                            const originalData = result.get(key)!;
                            result.set(key, {
                                ...originalData,
                                hidden: true,
                                color: overwriteData.color,
                                label: overwriteData.label,
                                type: overwriteData.type || originalData.type
                            });
                        }
                    }
                }
            } else {
                // Si no hay page, mantener el comportamiento anterior
                if (result.has(key)) {
                    const originalData = result.get(key)!;
                    result.set(key, {
                        ...originalData,
                        hidden: true,
                        color: overwriteData.color,
                        label: overwriteData.label,
                        type: overwriteData.type || originalData.type
                    });
                }

                result.forEach((value, mapKey) => {
                    if (mapKey && mapKey.startsWith(key)) {
                        result.set(mapKey, {
                            ...value,
                            hidden: true,
                            color: overwriteData.color,
                            label: overwriteData.label,
                            type: overwriteData.type || value.type
                        });
                    }
                });
            }
        }
    });

    // Luego procesamos las demás sobrescrituras
    overWrite.forEach((overwriteData, key) => {
        if (!overwriteData || !key) return; // Validación inicial

        if (!overwriteData.hiddenAll && result.has(key)) {
            const originalData = result.get(key)!;
            result.set(key, {
                ...originalData,
                ...(overwriteData.fontWeight !== undefined && { fontWeight: overwriteData.fontWeight }),
                ...(overwriteData.showBorder !== undefined && { showBorder: overwriteData.showBorder }),
                ...(overwriteData.hidden !== undefined && { hidden: overwriteData.hidden }),
                ...(overwriteData.color !== undefined && { color: overwriteData.color }),
                ...(overwriteData.label !== undefined && { label: overwriteData.label }),
                ...(overwriteData.type !== undefined && { type: overwriteData.type }),
                ...(overwriteData.value !== undefined && { value: overwriteData.value }),
                ...(overwriteData.order !== undefined && { order: overwriteData.order })
            });
        }
    });

    // Reordenar campos que tengan la propiedad `order` definida
    const hasOrderedFields = Array.from(result.values()).some(v => v.order !== undefined);
    if (hasOrderedFields) {
        const entries = Array.from(result.entries());
        const orderedResult = new Map<string, ConfirmationData>();

        // Agrupar entradas por prefijo (groupId) para reordenar dentro de cada grupo
        let i = 0;
        while (i < entries.length) {
            const [key, value] = entries[i];
            const groupId = key.includes('|') ? key.split('|')[0] : null;

            if (groupId) {
                // Recopilar todas las entradas del mismo grupo
                const groupEntries: [string, ConfirmationData][] = [];
                while (i < entries.length) {
                    const [k, v] = entries[i];
                    const currentGroupId = k.includes('|') ? k.split('|')[0] : null;
                    if (currentGroupId === groupId) {
                        groupEntries.push([k, v]);
                        i++;
                    } else {
                        break;
                    }
                }

                // Verificar si algún campo del grupo tiene order
                const hasOrder = groupEntries.some(([_, v]) => v.order !== undefined);
                if (hasOrder) {
                    // Separar campos con y sin order
                    const withOrder = groupEntries.filter(([_, v]) => v.order !== undefined);
                    const withoutOrder = groupEntries.filter(([_, v]) => v.order === undefined);

                    // Ordenar los que tienen order
                    withOrder.sort((a, b) => (a[1].order ?? 0) - (b[1].order ?? 0));

                    // Insertar primero los que tienen order, luego los que no
                    withOrder.forEach(([k, v]) => orderedResult.set(k, v));
                    withoutOrder.forEach(([k, v]) => orderedResult.set(k, v));
                } else {
                    groupEntries.forEach(([k, v]) => orderedResult.set(k, v));
                }
            } else {
                orderedResult.set(key, value);
                i++;
            }
        }

        return orderedResult;
    }

    return result;
};

export const processSectionsAndGroups = (
    page: PageJson,
    questionValueMap: Map<string, { value: string }>,
    language: 'en' | 'es' | 'pt',
    showBorderForLastField: boolean = false,
    showBorderForAllFields: boolean = false
): Map<string, ConfirmationData> => {
    const cd = new Map<string, ConfirmationData>();
    page.sections.forEach(section => {
        // Ocultar solo el título si contiene 'Proporciona más información' en español
        const shouldHideSectionTitle = section.title?.es?.includes('Proporciona más información');

        cd.set(section.section_id, { fontWeight: "bold", label: section.title[language], hidden: section.hidden || shouldHideSectionTitle });
        section.groups?.forEach(group => {
            if (group.title[language] && group.fields?.length > 0) {
                cd.set(group.group_id, { fontWeight: "bold", label: group.title[language], hidden: section.hidden || group.hidden });
            }
            group.fields?.forEach((field, index) => {
                if (field.question_id) {
                    // Filtrar campos LABEL y ADD para calcular el último campo real
                    const visibleFields = group.fields.filter(f => f.ui !== UI_TYPES.ADD && !f.hidden);
                    const isLastField = field === visibleFields[visibleFields.length - 1];
                    const key = `${group.group_id}|${field.question_id}`;
                    switch (field.ui) {
                        case UI_TYPES.ADD:
                            // Ignorar campos de tipo ADD
                            break;
                        case UI_TYPES.MULTIPLE_CHOICE:
                            field.options.forEach((option, optIndex) => {
                                const isLastOption = optIndex === field.options.length - 1;

                                cd.set(`${key}-opt${option.key}`, {
                                    label: option.value[language],
                                    value: questionValueMap.get(`${field.question_id}-opt${option.key}`)?.value,
                                    hidden: section.hidden || group.hidden || field.hidden,
                                    type: "checkbox",
                                    showBorder: showBorderForLastField ? (isLastField && isLastOption) : showBorderForAllFields
                                });
                            });
                            break;
                        case UI_TYPES.LABEL:
                            cd.set(key, {
                                label: field.title[language],
                                value: questionValueMap.get(key)?.value,
                                showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                hidden: true,
                                type: "text"
                            });
                            break;
                        case UI_TYPES.FILE_SEARCH:
                            cd.set(key, {
                                label: field.title[language],
                                value: questionValueMap.get(key)?.value,
                                showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                hidden: section.hidden || group.hidden || field.hidden,
                                type: "download"
                            });
                            break;
                        case UI_TYPES.DATEPICKER:
                        case UI_TYPES.DATEINPUTPICKER:
                            cd.set(key, {
                                label: field.title[language],
                                value: questionValueMap.get(key)?.value !== NO_DATA ? questionValueMap.get(key)?.value : getCurrentlyText(language),
                                showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                hidden: section.hidden || group.hidden || field.hidden,
                            });
                            break;

                        case UI_TYPES.AMOUNT_CONVERTER:
                            // Para AMOUNT_CONVERTER, agregar las dos entradas (anual y mensual)
                            const annualKey = `${key}-annual`;
                            const monthlyKey = `${key}-monthly`;
                            const annualData = questionValueMap.get(annualKey);
                            const monthlyData = questionValueMap.get(monthlyKey);

                            // Etiquetas según idioma
                            const annualLabel = language === 'es' ? 'Ingreso anual' : language === 'en' ? 'Annual Income' : 'Renda anual';
                            const monthlyLabel = language === 'es' ? 'Ingreso mensual' : language === 'en' ? 'Monthly Income' : 'Renda mensal';

                            if (annualData) {
                                cd.set(annualKey, {
                                    label: annualLabel,
                                    value: annualData.value,
                                    showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                    hidden: section.hidden || group.hidden || field.hidden,
                                    type: "text"
                                });
                            }

                            if (monthlyData) {
                                cd.set(monthlyKey, {
                                    label: monthlyLabel,
                                    value: monthlyData.value,
                                    showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                    hidden: section.hidden || group.hidden || field.hidden,
                                    type: "text"
                                });
                            }
                            break;
                        default:
                            cd.set(key, {
                                label: field.title[language],
                                value: questionValueMap.get(key)?.value,
                                showBorder: showBorderForLastField ? isLastField : showBorderForAllFields,
                                hidden: section.hidden || group.hidden || field.hidden,
                                type: field.title[language] === "LinkedIn URL" ? "link" : "text"
                            });

                            // Agregar totales específicos para la página de activos y pasivos
                            if (page.page_id === "f89bf9e3-9bb9-48ca-b539-d4da6838c9c1" && isLastField) {
                                const activosGroupId = "413ca9cb-58cf-4715-8628-6a9660e5104a";
                                const pasivosGroupId = "cfeae1ca-6fce-4781-a5bb-9f8b160576a4";

                                // Etiquetas según idioma
                                const totalActivosLabel = language === 'es' ? 'Total Activos' : language === 'en' ? 'Total Assets' : 'Total Ativos';
                                const totalPasivosLabel = language === 'es' ? 'Total Pasivos' : language === 'en' ? 'Total Liabilities' : 'Total Passivos';
                                const balanceLabel = language === 'es' ? 'Balance Activos/Pasivos' : language === 'en' ? 'Net Balance (Assets - Liabilities)' : 'Saldo Líquido (Ativos - Passivos)';

                                if (group.group_id === activosGroupId) {
                                    // Agregar total de activos después del último campo del grupo de activos
                                    const totalActivosData = questionValueMap.get(`${activosGroupId}|total-activos`);
                                    if (totalActivosData) {
                                        cd.set(`${activosGroupId}|total-activos`, {
                                            label: totalActivosLabel,
                                            value: totalActivosData.value,
                                            fontWeight: "bold",
                                            hidden: false,
                                            type: "text",
                                            showBorder: true
                                        });
                                    }
                                } else if (group.group_id === pasivosGroupId) {
                                    // Agregar total de pasivos después del último campo del grupo de pasivos
                                    const totalPasivosData = questionValueMap.get(`${pasivosGroupId}|total-pasivos`);
                                    if (totalPasivosData) {
                                        cd.set(`${pasivosGroupId}|total-pasivos`, {
                                            label: totalPasivosLabel,
                                            value: totalPasivosData.value,
                                            fontWeight: "bold",
                                            hidden: false,
                                            type: "text",
                                            showBorder: true
                                        });
                                    }

                                    // Agregar balance neto después del total de pasivos
                                    const balanceData = questionValueMap.get(`balance-neto`);
                                    if (balanceData) {
                                        cd.set(`balance-neto`, {
                                            label: balanceLabel,
                                            value: balanceData.value,
                                            fontWeight: "bold",
                                            hidden: false,
                                            type: "text",
                                            showBorder: true
                                        });
                                    }
                                }
                            }
                            break;
                    }
                }
            });
        });
    });



    return cd;
};

/**
 * Actualiza los títulos de todos los grupos dentro de una sección específica
 * @param page - La página JSON que contiene las secciones y grupos
 * @param sectionGroupTitles - Un mapa que contiene como clave el sectionId y como valor el nuevo título para los grupos
 * @param overWrite - El mapa de sobrescrituras existente que será actualizado
 * @param language - El idioma a utilizar ('en', 'es', 'pt')
 * @returns Un nuevo mapa con las sobrescrituras actualizadas
 */
export const updateGroupTitlesInSection = (
    page: PageJson | undefined,
    sectionGroupTitles: Record<string, string>,
    overWrite: Map<string, ConfirmationData> = new Map(),
    language: 'en' | 'es' | 'pt' = 'es'
): Map<string, ConfirmationData> => {
    if (!page) {
        return overWrite;
    }

    const result = new Map(overWrite);

    // Iterar sobre cada entrada en el mapa sectionGroupTitles
    Object.entries(sectionGroupTitles).forEach(([sectionId, newTitle]) => {
        // Encontrar la sección correspondiente
        const section = page.sections.find(section => section.section_id === sectionId);
        if (!section) {
            return;
        }

        // Ordenar los grupos por su propiedad 'order'
        const sortedGroups = [...section.groups].sort((a, b) => a.order - b.order);

        // Contador para grupos que tienen título
        let groupCounter = 1;

        // Para cada grupo en la sección, actualizar su título con número de orden
        sortedGroups.forEach((group) => {
            if (group && group.group_id) {
                // Solo actualizar si el grupo ya tiene un título
                const existingData = result.get(group.group_id);
                const hasTitle = group.title && (group.title[language] || existingData?.label);

                if (hasTitle) {
                    // Agregar o actualizar la sobrescritura para este grupo con número de orden
                    result.set(group.group_id, {
                        ...(existingData || {}),
                        label: `${newTitle} ${groupCounter}`,
                        fontWeight: "bold"
                    });

                    // Incrementar el contador solo para grupos con título
                    groupCounter++;
                }
            }
        });
    });

    return result;
};

export const updateSummaryPageInfo = (
    page: PageJson | undefined,
    overWrite: Map<string, ConfirmationData>,
    language: 'en' | 'es' | 'pt' = 'es',
    showBorderForLastField: boolean = false,
    showBorderForAllFields: boolean = false,
    sectionGroupTitles?: Record<string, string>,
    currency: string = 'USD $ '
): Map<string, ConfirmationData> => {
    if (page === undefined) {
        return new Map();
    }

    // Si se proporcionan títulos de grupos para secciones, actualizar el overWrite
    if (sectionGroupTitles) {
        overWrite = updateGroupTitlesInSection(page, sectionGroupTitles, overWrite, language);
    }

    const questionValueMap = createQuestionValueMap(page, language, currency);
    if (page.page_id === "2533618e-edf8-4ce8-85e2-bd58e3b8a67b") {
        const lastNaKey = "4ea018bf-7570-4e4c-adc5-617077134715-opt5";
        if (questionValueMap.get(lastNaKey)) {
            const existingQuestion = questionValueMap.get(lastNaKey);
            questionValueMap.set(lastNaKey, {
                value: existingQuestion?.value === "SI" &&
                    questionValueMap.get("266cb0ed-4d3c-4b0e-b75f-098d9f00ce8c-opt5")?.value === "SI" &&
                    questionValueMap.get("ad1e32bd-6976-4068-80c1-6ccbd441e1ed-opt5")?.value === "SI" ? "SI" : "NO",
                hidden: existingQuestion?.hidden || false,
                label: "Nome"
            });
        }
    }

    if (page.page_id === "5cd6641e-0210-4ee1-849e-c36c38dd89b9") {
        const lastNaKey = "4ea018bf-7570-4e4c-adc5-617077134715-opt5";
        if (questionValueMap.get(lastNaKey)) {
            const existingQuestion = questionValueMap.get(lastNaKey);
            questionValueMap.set(lastNaKey, {
                value: existingQuestion?.value === "SI" &&
                    questionValueMap.get("d97c485f-51db-40fe-8304-6cc457a095a4-opt5")?.value === "SI" &&
                    questionValueMap.get("da2be449-bf78-4c96-bd5c-6ae932cb440c-opt5")?.value === "SI" ? "SI" : "NO",
                hidden: existingQuestion?.hidden || false,
                label: "Nome"
            });
        }
    }

    if (page.page_id === "f89bf9e3-9bb9-48ca-b539-d4da6838c9c1") {
        const activosGroupId = "413ca9cb-58cf-4715-8628-6a9660e5104a";
        const pasivosGroupId = "cfeae1ca-6fce-4781-a5bb-9f8b160576a4";

        // IDs de campos de activos
        const activosIds = [
            "1d5e1d67-baa9-4733-aa32-0d70548f189e", // Propiedad residencial
            "f1cf8f70-cbf6-4fcc-a087-a0316c08a725", // Otras propiedades
            "a8afa725-5d58-45be-945a-e79924e7497d", // Efectos personales
            "9287d629-98ca-49fe-af4b-1c162cb6982c", // Inversiones
            "48565545-5243-43ab-ab0d-1332a38da0ca", // Efectivo
            "dda0e8b2-057c-4a3e-9c19-b9a57bc667ad"  // Otros
        ];

        // IDs de campos de pasivos
        const pasivosIds = [
            "194cddc1-09d5-4505-807f-96efdabc4d89", // Hipotecas
            "8ff72009-ab23-43aa-84c5-0e4dd4834174", // Préstamos
            "dda0e8b2-057c-4a3e-9c19-b9a57bc667ad"  // Otros (mismo ID que en activos)
        ];

        // Calcular suma de activos
        let totalActivos = 0;
        activosIds.forEach(id => {
            const key = `${activosGroupId}|${id}`;
            const value = questionValueMap.get(key)?.value;
            if (value && value !== NO_DATA) {
                // Remover el símbolo de moneda y convertir a número
                const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
                totalActivos += numericValue;
            }
        });

        // Calcular suma de pasivos
        let totalPasivos = 0;
        pasivosIds.forEach(id => {
            const key = `${pasivosGroupId}|${id}`;
            const value = questionValueMap.get(key)?.value;
            if (value && value !== NO_DATA) {
                // Remover el símbolo de moneda y convertir a número
                const numericValue = parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
                totalPasivos += numericValue;
            }
        });

        // Calcular balance (activos - pasivos)
        const balance = totalActivos - totalPasivos;

        // Etiquetas según idioma
        const totalActivosLabel = language === 'es' ? 'Total Activos' : language === 'en' ? 'Total Assets' : 'Total Ativos';
        const totalPasivosLabel = language === 'es' ? 'Total Pasivos' : language === 'en' ? 'Total Liabilities' : 'Total Passivos';
        const balanceLabel = language === 'es' ? 'Balance Activos/Pasivos' : language === 'en' ? 'Net Balance (Assets - Liabilities)' : 'Saldo Líquido (Ativos - Passivos)';

        // Agregar totales al map
        questionValueMap.set(`${activosGroupId}|total-activos`, {
            value: `${currency}${totalActivos.toLocaleString()}`,
            hidden: false,
            label: totalActivosLabel,
        });

        questionValueMap.set(`${pasivosGroupId}|total-pasivos`, {
            value: `${currency}${totalPasivos.toLocaleString()}`,
            hidden: false,
            label: totalPasivosLabel
        });

        questionValueMap.set(`balance-neto`, {
            value: `${currency}${balance.toLocaleString()}`,
            hidden: false,
            label: balanceLabel
        });
    }

    const cd = processSectionsAndGroups(page, questionValueMap, language, showBorderForLastField, showBorderForAllFields);
    const result = applyOverwrites(cd, overWrite, page);
    // Encontrar el último campo visible y quitarle el borde
    let lastVisibleKey: string | null = null;
    result.forEach((value, key) => {
        if (!value.hidden) {
            lastVisibleKey = key;
        }
    });
    if (lastVisibleKey) {
        const lastValue = result.get(lastVisibleKey);
        if (lastValue) {
            result.set(lastVisibleKey, {
                ...lastValue,
                showBorder: false
            });
        }
    }
    return result;
}